import React, { useState } from 'react';
import { Download, Plus, Music as MusicIcon } from 'lucide-react';
import TrackVariantsPlayer, { PRESETS } from './TrackVariantsPlayer';

/**
 * BandLab-style studio: left sidebar with imported songs, right side with the
 * active track's player + presets grid. Click a song in the sidebar to switch.
 */
export default function BandLabStudio({ job, onPublished, onRefresh }) {
  const tracks = job?.tracks || [];
  const [activeId, setActiveId] = useState(tracks[0]?.id || null);
  const active = tracks.find(t => t.id === activeId) || tracks[0];

  if (!tracks.length) {
    return <p className="text-sm text-white/50 text-center py-8">No hay tracks en este lote.</p>;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4 bg-black/30 rounded-xl overflow-hidden" data-testid="bandlab-studio">
      {/* ── LEFT SIDEBAR — Imported songs list ─────────────────────── */}
      <div className="border-r border-white/5 lg:max-h-[700px] lg:overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 sticky top-0 bg-black/60 backdrop-blur z-10">
          <h4 className="text-xs uppercase tracking-wider text-white/60 font-bold">Canciones importadas</h4>
          <span className="text-[10px] text-white/30">{tracks.length}</span>
        </div>
        <div>
          {tracks.map((t, idx) => {
            const isActive = activeId === t.id;
            const presetName = (PRESETS.find(p => p.id === (t.preset_id || ''))?.name || t.preset_id || '—');
            const completedVariants = Object.values(t.variants || {}).filter(v => v?.status === 'completed').length;
            const total = 8;
            const pct = Math.round((completedVariants / total) * 100);
            return (
              <button
                key={t.id}
                onClick={() => setActiveId(t.id)}
                className={`w-full text-left px-4 py-2.5 border-b border-white/5 transition flex items-center gap-3 ${
                  isActive ? 'bg-white/[0.06]' : 'hover:bg-white/[0.03]'
                }`}
                data-testid={`sidebar-track-${t.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {idx + 1}. {(t.publish_meta?.title || t.filename || '').replace(/\.[^.]+$/, '')}
                  </p>
                  <p className="text-[11px] text-white/40 truncate">
                    {presetName} · {pct}%
                    {t.published_track_id && <span className="text-green-400 ml-1">· Publicado</span>}
                  </p>
                </div>
                <Download className="w-3.5 h-3.5 text-white/30 hover:text-white/60" />
              </button>
            );
          })}
        </div>
      </div>

      {/* ── RIGHT MAIN — Active track player + presets ─────────────── */}
      <div className="p-4 lg:p-5">
        {active ? (
          <TrackVariantsPlayer
            key={active.id}
            track={active}
            jobId={job.id}
            mode="admin"
            isStaff={true}
            onPublished={onPublished}
            onRefresh={onRefresh}
          />
        ) : (
          <div className="flex items-center justify-center h-full py-20 text-white/40">
            <MusicIcon className="w-6 h-6 mr-2" /> Selecciona una canción
          </div>
        )}
      </div>
    </div>
  );
}
